import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    this.addEventListener('slideshow:select', this.#seguiMiniatura, { signal });
    this.#tieniIlFormato(target, signal);
  }

  /**
   * 01/10/2026 — ALL'APERTURA LA FOTO DEL FORMATO SCELTO NON SI PERDE.
   * Con le miniature a ordine fisso la foto della variante preselezionata (o
   * di `?variant=`) non e' la prima: ce la porta `pcr-miniatura-formato.js`
   * (`corrente` nei dati del Liquid). Ma lo slideshow del tema, nel fotogramma
   * in cui finisce di montarsi, se l'IntersectionObserver non ha ancora
   * contato le slide visibili chiama `select(0)`: quando quel fotogramma
   * arriva DOPO il posizionamento, la galleria torna sulla prima foto mentre
   * il menu dice un altro formato. Misurato senza scatti (che falsano: vedi la
   * memoria piacersi-cls-home-0110): 5 `select(0)` in 30 aperture, 2 rimaste
   * sulla foto sbagliata (412 e 1440, prima pagina e seconda).
   * Qui si ascolta `slideshow:select` (risale dallo slideshow): finche' il
   * cliente non ha messo le mani sulla scheda, se lo slideshow PRINCIPALE si
   * sposta da `corrente` lo si riporta li' nel fotogramma dopo, senza
   * animazione. La guardia si spegne al primo gesto (tocco, clic, rotella,
   * tasto, cambio di formato) e comunque 4 s dopo `load`: da li' in poi ogni
   * spostamento e' voluto. Nessuna lettura di geometria, un ascoltatore solo.
   * @param {Element | null} sezione
   * @param {AbortSignal} signal
   */
  #tieniIlFormato(sezione, signal) {
    const guardia = new AbortController();
    const spegni = () => guardia.abort();
    signal.addEventListener('abort', spegni, { once: true });
    const opzioni = { signal: guardia.signal, passive: true, once: true };
    for (const tipo of ['pointerdown', 'touchstart', 'wheel', 'keydown']) this.addEventListener(tipo, spegni, opzioni);
    sezione?.addEventListener('change', spegni, opzioni);
    const scadenza = () => window.setTimeout(spegni, 4000);
    if (document.readyState === 'complete') scadenza();
    else window.addEventListener('load', scadenza, opzioni);

    /** @type {number | null} */
    let voluto = null;
    this.addEventListener(
      'slideshow:select',
      (event) => {
        const slideshow = event.target;
        if (!(slideshow instanceof Element) || slideshow.closest('media-gallery') !== this) return;
        if (slideshow.closest('dialog, zoom-dialog')) return; // la galleria dello zoom ha il suo slideshow
        if (voluto === null) {
          // i dati si leggono qui e non alla connessione: quando l'elemento si
          // collega il parser non ha ancora letto i suoi figli
          try {
            voluto = Number(JSON.parse(this.querySelector('script[data-pcr-formati]')?.textContent || '{}').corrente) || 0;
          } catch {
            voluto = 0;
          }
        }
        if (!voluto) return spegni();
        const indice = /** @type {any} */ (event).detail?.index;
        if (indice === voluto) return;
        requestAnimationFrame(() => {
          if (guardia.signal.aborted || !slideshow.isConnected) return;
          const s = /** @type {any} */ (slideshow);
          if (s.current !== voluto && typeof s.select === 'function') s.select(voluto, undefined, { animate: false });
        });
      },
      { signal: guardia.signal }
    );
  }

  /**
   * 30/09/2026 — SU TELEFONO LA COLONNA DELLE MINIATURE SEGUE IL DITO.
   * Da oggi il telefono ha le miniature in colonna a sinistra come il computer
   * (product-page-enhance.css, 11.4). Quando si sceglie con una miniatura o col
   * formato, il tema porta gia' la miniatura in vista (`select` →
   * `#centerSelectedThumbnail`); quando invece si STRISCIA la foto grande il
   * tema non lo fa, e oltre la quinta foto la miniatura accesa finiva fuori
   * dalla colonna. Qui si rimedia solo per lo scorrimento a dito, solo su
   * telefono e solo se la colonna scorre davvero (oltre 5 foto): una lettura
   * di geometria per cambio di foto, mai per fotogramma.
   * @param {Event & { detail?: { trigger?: string, index?: number } }} event
   */
  #seguiMiniatura = (event) => {
    if (event.detail?.trigger !== 'scroll') return;
    if (!window.matchMedia('(max-width: 749px)').matches) return;
    const colonna = this.querySelector('slideshow-controls[thumbnails] .slideshow-controls__thumbnails-container');
    if (!(colonna instanceof HTMLElement) || colonna.scrollHeight <= colonna.clientHeight + 1) return;
    const miniatura = colonna.querySelectorAll('.slideshow-controls__thumbnail')[event.detail.index ?? -1];
    if (!miniatura) return;
    const c = colonna.getBoundingClientRect();
    const m = miniatura.getBoundingClientRect();
    if (m.top >= c.top && m.bottom <= c.bottom) return;
    const lento = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    colonna.scrollTo({
      top: colonna.scrollTop + (m.top - c.top) - (c.height - m.height) / 2,
      behavior: lento ? 'instant' : 'smooth',
    });
  };

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * L'ORIGINALE FACEVA SOLO `replaceWith`, E SI VEDEVA.
   * Una sostituzione secca del nodo e' istantanea: la vecchia foto sparisce e
   * la nuova appare nello stesso frame. Sullo schermo non si legge come "il
   * prodotto e' cambiato", si legge come uno sfarfallio — soprattutto qui,
   * dove con `hide_variants` la galleria di un prodotto a piu' formati mostra
   * una sola immagine e quindi non c'e' nessuno scorrimento di carosello a
   * raccontare il passaggio.
   *
   * Ora la nuova immagine ENTRA DA DESTRA mentre compare. Tre scelte dietro:
   *
   *   · SI ANIMA SOLO IL RIQUADRO DELLA FOTO, non tutta la galleria. Con le
   *     miniature a sinistra, animare l'intero elemento farebbe scivolare
   *     anche quelle, e le miniature non stanno cambiando: sono il contesto
   *     fermo rispetto a cui si capisce che e' cambiata la foto grande.
   *   · SI SALTA L'ANIMAZIONE SE LA FOTO E' LA STESSA. Molti prodotti hanno
   *     formati senza una foto propria: li' il ricambio non cambia nulla, e
   *     animare un'immagine identica sembrerebbe un difetto di rendering.
   *   · WEB ANIMATIONS API E NON UNA CLASSE CSS. Niente stato da ripulire, e
   *     nessun rischio che una classe rimasta appesa lasci la galleria
   *     trasparente se l'animazione viene interrotta da un secondo cambio
   *     rapido di variante.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    /* 23/09/2026 sera — STESSE FOTO, STESSA GALLERIA.
       Con le miniature a ordine fisso (`hide_variants` spento) il server
       rimanda la stessa identica galleria, cambia solo quale slide e' «la
       corrente». Buttarla e rifarla voleva dire: foto da ridecodificare, un
       fotogramma sulla slide 0 prima del riposizionamento, e l'ingresso da
       destra sopra la dissolvenza che `pcr-miniatura-formato.js` ha gia'
       fatto al tocco. Qui si aggiornano i dati e, se serve, si porta il
       carosello sulla slide giusta. Foto diverse (galleria filtrata per
       variante, prodotto in evidenza): il percorso di prima, invariato. */
    const datiVecchi = this.querySelector('script[data-pcr-formati]');
    const datiNuovi = newMediaGallery.querySelector('script[data-pcr-formati]');
    if (datiVecchi && datiNuovi) {
      try {
        const vecchi = JSON.parse(datiVecchi.textContent || '{}');
        const nuovi = JSON.parse(datiNuovi.textContent || '{}');
        if (Array.isArray(vecchi.media) && String(vecchi.media) === String(nuovi.media)) {
          datiVecchi.textContent = datiNuovi.textContent;
          const indice = Number(nuovi.corrente) || 0;
          const slideshow = this.slideshow;
          // slide 0 = formato senza foto propria oppure foto gia' prima: si
          // resta dove si e' (il tocco ha gia' portato la galleria li')
          if (indice > 0 && slideshow && slideshow.current !== indice) {
            const dissolvi = /** @type {any} */ (window).pcrDissolvi;
            if (typeof dissolvi === 'function') dissolvi(this, indice);
            else slideshow.select(indice, undefined, { animate: false });
          }
          return;
        }
      } catch {
        // dati illeggibili: si ricade sulla sostituzione
      }
    }

    const primaSrc = MediaGallery.#fotoPrincipale(this);

    this.replaceWith(newMediaGallery);

    const dopoSrc = MediaGallery.#fotoPrincipale(newMediaGallery);
    if (primaSrc && dopoSrc && primaSrc === dopoSrc) return;

    MediaGallery.animaIngresso(newMediaGallery);
  };

  /**
   * Sorgente dell'immagine grande attualmente mostrata, per confronto.
   * Si usa `getAttribute('src')` e non `currentSrc`: quest'ultimo dipende da
   * quale sorgente del `srcset` il browser ha scelto, quindi due immagini
   * identiche possono avere `currentSrc` diversi a seconda del momento in cui
   * si legge, e il confronto direbbe "cambiata" quando non lo e'.
   *
   * @param {Element} gallery
   * @returns {string | null}
   */
  static #fotoPrincipale(gallery) {
    const img = gallery?.querySelector('.product-media-container img');
    return img?.getAttribute('src') ?? null;
  }

  /**
   * Fa entrare da destra il riquadro della foto grande.
   * @param {Element} gallery
   */
  static animaIngresso(gallery) {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    /* IL BERSAGLIO E' LA PRIMA SLIDE, E LA SCELTA E' MISURATA.
       Struttura reale della galleria (letta in pagina, non ipotizzata):

           slideshow-component
             slideshow-container        ← ritaglia
               slideshow-slides         ← STRISCIA con TUTTE le slide affiancate
                 slideshow-slide        ← [118,188]  la foto mostrata
                   .product-media-constraint-wrapper
                 slideshow-slide        ← [720,188]  fuori dal ritaglio
             slideshow-controls         ← le miniature a sinistra

       Il primo tentativo animava `slideshow-slides`: sbagliato due volte.
       E' la striscia scorrevole del carosello, quindi le si mette una
       `transform` addosso mentre il componente la governa con lo scorrimento —
       due padroni per la stessa proprieta'. E muoverebbe anche le slide
       vicine, cioe' esattamente cio' che il carosello deve poter fare da solo.

       La slide mostrata dopo il cambio variante e' SEMPRE la prima: il Liquid
       ordina i media mettendo davanti quello della variante selezionata. Si
       anima il suo involucro interno: nessuna interferenza con il carosello,
       nessun effetto sul calcolo dello scorrimento (una `transform` non tocca
       il layout), e le miniature — che stanno in un altro ramo — restano
       ferme, come devono, perche' sono il riferimento immobile rispetto a cui
       si capisce che e' cambiata la foto grande. */
    const scena =
      gallery.querySelector('slideshow-slide .product-media-constraint-wrapper') ??
      gallery.querySelector('.product-media-container') ??
      gallery;

    if (!(scena instanceof HTMLElement)) return;

    // Su telefono la corsa e' piu' corta: il 6% di 350px sono 21px e si
    // leggono appena, mentre lo stesso 6% su 680 ne fa 41 ed e' il movimento
    // giusto. Si ragiona in percentuale proprio per questo.
    const stretto = window.matchMedia('(max-width: 749px)').matches;

    scena.animate(
      [
        { opacity: 0, transform: `translate3d(${stretto ? 9 : 6}%, 0, 0) scale(0.985)` },
        { opacity: 1, transform: 'none' },
      ],
      {
        duration: stretto ? 420 : 520,
        easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
      }
    );
    // Nessun `fill`: a fine corsa l'elemento torna da solo al proprio stato
    // naturale e non resta niente da ripulire. Un `fill: both` lascerebbe
    // l'animazione a comandare `transform` e `opacity` per sempre, e da quel
    // momento qualunque altro stile su quelle due proprieta' — per esempio
    // l'ingrandimento al passaggio del mouse — smetterebbe di funzionare.
    // Il primo fotogramma non lampeggia perche' `animate()` viene chiamata
    // nello stesso compito di `replaceWith`: fra le due non c'e' un disegno.
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
